import { normalizeClassList } from '../../../NewBlogSystem/utilities/htmlEditorUtils.js';

export const MIN_HEADING_CONTRAST = 4.5;
export const AI_HEADING_DARK_CLASS = 'ai-heading-contrast-dark';
export const AI_HEADING_LIGHT_CLASS = 'ai-heading-contrast-light';

const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
const BOOTSTRAP_COLORS = Object.freeze({
  primary: '#0d6efd', secondary: '#6c757d', success: '#198754', danger: '#dc3545',
  warning: '#ffc107', info: '#0dcaf0', light: '#f8f9fa', dark: '#212529',
  white: '#ffffff', black: '#000000', body: '#212529', 'body-secondary': '#6c757d',
  'body-tertiary': '#dee2e6', transparent: 'transparent',
});

const clamp = (value) => Math.max(0, Math.min(255, value));

export const parseCssColor = (value) => {
  const input = String(value || '').trim().toLowerCase().replace(/\s*!important\s*$/, '');
  if (!input || input === 'transparent') return null;
  const shortHex = input.match(/^#([\da-f]{3,4})$/i);
  if (shortHex) {
    const digits = shortHex[1].split('').map((digit) => parseInt(`${digit}${digit}`, 16));
    return [...digits.slice(0, 3), digits.length === 4 ? digits[3] / 255 : 1];
  }
  const longHex = input.match(/^#([\da-f]{6})([\da-f]{2})?$/i);
  if (longHex) {
    const rgb = [0, 2, 4].map((offset) => parseInt(longHex[1].slice(offset, offset + 2), 16));
    return [...rgb, longHex[2] ? parseInt(longHex[2], 16) / 255 : 1];
  }
  const rgb = input.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+)%?)?\s*\)$/);
  if (!rgb) return null;
  const alphaValue = rgb[4] === undefined ? 1 : Number(rgb[4]);
  const alpha = input.includes(`${rgb[4]}%`) ? alphaValue / 100 : alphaValue;
  return [clamp(Number(rgb[1])), clamp(Number(rgb[2])), clamp(Number(rgb[3])), Math.max(0, Math.min(1, alpha))];
};

const composite = (foreground, background) => {
  if (!foreground) return background;
  if (foreground[3] >= 1) return foreground;
  const alpha = foreground[3];
  return [
    foreground[0] * alpha + background[0] * (1 - alpha),
    foreground[1] * alpha + background[1] * (1 - alpha),
    foreground[2] * alpha + background[2] * (1 - alpha),
    1,
  ];
};

const luminance = (color) => {
  const channels = color.slice(0, 3).map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};

export const contrastRatio = (foreground, background) => {
  const foregroundLuminance = luminance(composite(foreground, background));
  const backgroundLuminance = luminance(background);
  return (Math.max(foregroundLuminance, backgroundLuminance) + 0.05)
    / (Math.min(foregroundLuminance, backgroundLuminance) + 0.05);
};

const bootstrapColor = (classes, prefix) => {
  const match = [...classes].reverse().find((className) => className.startsWith(prefix));
  return match ? parseCssColor(BOOTSTRAP_COLORS[match.slice(prefix.length)]) : null;
};

const resolveNodeColors = (node, classes, inherited) => {
  const classNames = normalizeClassList(node?.attributes?.class);
  let foreground = bootstrapColor(classNames, 'text-') || inherited.foreground;
  let background = bootstrapColor(classNames, 'bg-');
  classNames.forEach((className) => {
    const style = classes?.[className]?.base;
    foreground = parseCssColor(style?.color) || foreground;
    background = parseCssColor(style?.backgroundColor || style?.['background-color']) || background;
  });
  return {
    foreground,
    background: background ? composite(background, inherited.background) : inherited.background,
  };
};

export const repairHeadingContrast = (editorDocument) => {
  if (!editorDocument?.root) return editorDocument;
  let usedDarkRepair = false;
  let usedLightRepair = false;
  const visit = (node, inherited) => {
    if (!node || node.type === 'text') return node;
    const resolved = resolveNodeColors(node, editorDocument.classes || {}, inherited);
    let attributes = node.attributes;
    if (HEADING_TAGS.has(node.type) && contrastRatio(resolved.foreground, resolved.background) < MIN_HEADING_CONTRAST) {
      const blackRatio = contrastRatio([0, 0, 0, 1], resolved.background);
      const whiteRatio = contrastRatio([255, 255, 255, 1], resolved.background);
      const repairClass = blackRatio >= whiteRatio ? AI_HEADING_DARK_CLASS : AI_HEADING_LIGHT_CLASS;
      usedDarkRepair ||= repairClass === AI_HEADING_DARK_CLASS;
      usedLightRepair ||= repairClass === AI_HEADING_LIGHT_CLASS;
      attributes = { ...node.attributes, class: normalizeClassList([...(node.attributes?.class || []), repairClass]) };
      resolved.foreground = blackRatio >= whiteRatio ? [0, 0, 0, 1] : [255, 255, 255, 1];
    }
    return { ...node, attributes, children: (node.children || []).map((child) => visit(child, resolved)) };
  };
  const root = visit(editorDocument.root, { foreground: [33, 37, 41, 1], background: [255, 255, 255, 1] });
  if (!usedDarkRepair && !usedLightRepair) return { ...editorDocument, root };
  const classes = { ...(editorDocument.classes || {}) };
  if (usedDarkRepair) classes[AI_HEADING_DARK_CLASS] = { base: { color: '#000000' }, sm: {}, md: {}, lg: {}, xl: {} };
  if (usedLightRepair) classes[AI_HEADING_LIGHT_CLASS] = { base: { color: '#ffffff' }, sm: {}, md: {}, lg: {}, xl: {} };
  return { ...editorDocument, root, classes };
};
